import { type FC } from 'react';
import styled from 'styled-components';

import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

interface Props {
  fundPercent: number;
  benchmarkPercent?: number;
}

/**
 * Display two bars with a gray background and a blue bar on top.
 * They gray background is always 100% wide while the blue bar
 * will fill it up from the specified input value (0% to 100%).
 *
 * @example
 * Fund: 37%
 * ████████████░░░░░░░░░░░░░
 *
 * Benchmark: 26%
 * █████████░░░░░░░░░░░░░░░░
 */
export const ScienceBasedTargetsChart: FC<Props> = ({
  fundPercent: fundValue,
  benchmarkPercent: benchmarkValue,
}) => {
  const texts = useTexts();

  return (
    <WithGapStyled gap="24">
      <WithGap gap="16" direction="row">
        <ValueText className="font-body-demibold">{fundValue} %</ValueText>

        <WithGapStyled gap="8">
          <span className="font-body-demibold">
            {texts.pages.fundEsg.fund.title}
          </span>

          <BarContainer>
            <WithGrayBackground />
            <SingleBar value={fundValue} color="var(--p-color-categorical-1)" />
          </BarContainer>
        </WithGapStyled>
      </WithGap>

      {benchmarkValue && (
        <WithGap gap="16" direction="row">
          <ValueText className="font-body-demibold">
            {benchmarkValue} %
          </ValueText>

          <WithGapStyled gap="8">
            <span className="font-body-demibold">
              {texts.pages.fundEsg.benchmark.title}
            </span>

            <BarContainer>
              <WithGrayBackground />
              <SingleBar
                value={benchmarkValue}
                color="var(--p-color-categorical-2)"
              />
            </BarContainer>
          </WithGapStyled>
        </WithGap>
      )}
    </WithGapStyled>
  );
};

const ValueText = styled.span`
  /**
   * Make sure that both the fund and benchmark values have the same
   * width, so that the bars are properly vertically aligned.
   * This width should be good enough for up to four digits.
   */
  min-width: 75px;

  text-align: right;
  margin-bottom: 4px;
  align-self: flex-end;
`;

const WithGapStyled = styled(WithGap)`
  flex: 1;
  width: 100%;
`;

const BarContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  width: 100%;
`;

interface SingleBarProps {
  value: number;
  color: string;
}

const SingleBar = styled.div<SingleBarProps>`
  width: ${(props) => props.value}%;
  height: 32px;
  background-color: ${(props) => props.color};
  border: 1px solid var(--p-color-stroke-subtle-0);

  grid-column: 1;
  grid-row: 1;
`;

const WithGrayBackground = styled.div`
  width: 100%;
  height: 32px;
  background-color: var(--p-color-surface-1);
  border: 1px solid var(--p-color-stroke-subtle-0);

  grid-column: 1;
  grid-row: 1;
`;
